/**
 * How an attendance flag reads in the Checkpoint Console status system.
 *
 * The tone is not decoration — it is the same language the rest of the app speaks:
 * teal = the day went as scheduled (`onTime`); red = the day is missing (`absent`);
 * amber = attention, something is off but the person was here (late, early leave,
 * overtime, an incomplete pair); grey = neutral information that isn't a verdict
 * (arrived early, worked off-schedule). A green "on time" can never be mistaken for
 * a red "absent".
 */
import type {StatusTone} from '@/shared/ui/status';
import type {AttendanceFlag} from '@/entities/me/types';

/** The flags sv_control emits (attendance.rs). Anything outside this set is ignored. */
const KNOWN: readonly AttendanceFlag[] = [
  'absent',
  'unscheduled',
  'incomplete',
  'late',
  'earlyArrival',
  'earlyLeave',
  'overtime',
  'onTime',
];

const TONES: Record<AttendanceFlag, StatusTone> = {
  onTime: 'granted',
  overtime: 'pending',
  late: 'pending',
  earlyLeave: 'pending',
  incomplete: 'pending',
  earlyArrival: 'neutral',
  unscheduled: 'neutral',
  absent: 'denied',
};

export function flagTone(flag: AttendanceFlag): StatusTone {
  return TONES[flag];
}

export function flagLabelKey(flag: AttendanceFlag): string {
  return `attendance.flag.${flag}`;
}

/** Keep only the flags the app knows how to render — an unknown one must not crash. */
export function knownFlags(flags: string[]): AttendanceFlag[] {
  return flags.filter((f): f is AttendanceFlag =>
    (KNOWN as readonly string[]).includes(f),
  );
}
